/**
 * ChatMessagesArea — Messages display area with loading, agent selector, and message list.
 *
 * New design: clean conversation area with gap between messages,
 * floating scroll-to-bottom button.
 */

import React, { useRef, useEffect, useState, useCallback } from "react";
import Lottie from "lottie-react";
import animationData from "../assets/cogsol-loading.json";
import { MessageList } from "./MessageList";
import { ThinkingIndicator } from "./ThinkingIndicator";
import { AgentSelector } from "./AgentSelector";
import { ArrowDown } from "lucide-react";

const ChatMessagesArea = ({
  // Loading & agent selection
  loading,
  activeRetrievalId,
  retrievals,
  onRetrievalChange,
  envRetrieval,
  isEmbedded,
  // Audio realtime
  isConnected,
  items,
  renderAudioMessage,
  // Message list props
  messages,
  iniMessage,
  assistantColors,
  urlLogo,
  chatName,
  executionStatus,
  waitingOAuthServerId,
  chatId,
  onReauthorize,
  expandedReferences,
  onToggleReferences,
  attachments,
  sentFileMetadata,
  messageMetadataList,
  allowMetadata,
  onFileMetadataClick,
  // Matrix
  activatingMatrix,
}) => {
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [showScrollBtn, setShowScrollBtn] = useState(false);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "end",
    });
  }, [messages]);

  // Track scroll position to show/hide scroll-to-bottom button
  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowScrollBtn(distanceFromBottom > 100);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="flex-1 relative min-h-0">
      <div
        ref={scrollContainerRef}
        className={`h-full px-4 scroll-smooth ${activeRetrievalId ? "py-6 overflow-y-auto" : "flex flex-col"}`}
        onScroll={handleScroll}
        // translateZ(0): one compositor layer, avoids SVG repaint trails on scroll.
        style={{ scrollbarWidth: "thin", scrollbarColor: "#ddd transparent", transform: "translateZ(0)" }}
      >
        {loading && (
          <div className="flex items-center justify-center pt-24">
            <Lottie
              animationData={animationData}
              className="h-40 w-40"
            />
          </div>
        )}
        {!loading && !activeRetrievalId ? (
          <AgentSelector
            retrievals={retrievals}
            onChange={onRetrievalChange}
            disabled={envRetrieval}
            variant="full"
          />
        ) : (
          !loading && (
            <>
              {isConnected
                ? items.map((item) => renderAudioMessage(item))
                : (
                  <MessageList
                    ref={messagesEndRef}
                    messages={messages}
                    iniMessage={iniMessage}
                    assistantColors={assistantColors}
                    urlLogo={urlLogo}
                    chatName={chatName}
                    executionStatus={executionStatus}
                    waitingOAuthServerId={waitingOAuthServerId}
                    chatId={chatId}
                    onReauthorize={onReauthorize}
                    expandedReferences={expandedReferences}
                    onToggleReferences={onToggleReferences}
                    attachments={attachments}
                    sentFileMetadata={sentFileMetadata}
                    messageMetadataList={messageMetadataList}
                    allowMetadata={allowMetadata}
                    onFileMetadataClick={onFileMetadataClick}
                  />
                )}
              {activatingMatrix && (
                <ThinkingIndicator
                  assistantColors={assistantColors}
                  urlLogo={urlLogo}
                  chatName={chatName}
                />
              )}
            </>
          )
        )}
      </div>

      {/* Scroll to bottom button — only when viewing messages, not agent selector */}
      {showScrollBtn && activeRetrievalId && messages.length > 0 && (
        <button
          type="button"
          className="absolute bottom-4 left-1/2 -translate-x-1/2 w-9 h-9 rounded-lg border bg-white shadow-md cursor-pointer flex items-center justify-center hover:bg-gray-50 transition-all z-10"
          style={{
            color: assistantColors?.primaryColor || "#6b7280",
            borderColor: assistantColors?.secondaryColor || "#e5e7eb",
          }}
          onClick={scrollToBottom}
          aria-label="Scroll to bottom"
        >
          <ArrowDown size={18} />
        </button>
      )}
    </div>
  );
};

export { ChatMessagesArea };
export default ChatMessagesArea;
